import { findCoveringSubscription } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"

import { Table, TopBar } from "@organisms/index"

import { useBrandItemsSubscriptionsQuery } from "@pages/Catalogs/BrandItems/BrandItems.store"
import { CatalogBrandItemRow } from "@pages/Catalogs/BrandItems/BrandItems.types"
import { getCategoryTitle } from "@pages/Catalogs/BrandItems/BrandItems.utils"
import { BrandItemsTableViewProps } from "@pages/Catalogs/BrandItems/views/BrandItemsTableView/BrandItemsTableView.types"
import BrandItemsTableViewTabs from "@pages/Catalogs/BrandItems/views/BrandItemsTableView/BrandItemsTableViewTabs"
import { useSubscribeMutation } from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"

function SubscribeItemCell(props: { row: CatalogBrandItemRow; catalogId: string }) {
    const { row, catalogId } = props

    const subscriptionsQuery = useBrandItemsSubscriptionsQuery(Number(catalogId))
    const subscribeMutation = useSubscribeMutation()

    const subscriptions = subscriptionsQuery.data ?? []
    // Coverage is derived off the shared subscriptions listing rather than baked onto the row, so a
    // subscription change elsewhere reflects here as soon as that cache invalidates.
    const coveringSubscription = findCoveringSubscription(subscriptions, {
        id: row.id,
        brand: row.brand,
        categories: row.categories,
    })

    // Hold the cell blank until coverage is known so a covered item never flashes an unsubscribed state.
    if (subscriptionsQuery.isLoading) {
        return null
    }

    if (coveringSubscription !== null) {
        // Unsubscribing needs the coverage context a row cannot show — which subscription owns the
        // item and how broad it is — so that action lives on the item details page.
        return <Table.Cells.BadgesCell badges={["Subscribed"]} />
    }

    const handleSubscribe = () => {
        subscribeMutation.mutate({ catalog: Number(catalogId), level: "item", catalog_item: row.id })
    }

    return (
        <Table.Cells.ButtonCell
            variant="subtle"
            leftIcon={faIcon({ name: "circle-plus", style: "regular" })}
            onClick={handleSubscribe}
            isProcessing={subscribeMutation.isPending}
            isDisabled={subscribeMutation.isPending}
        >
            Subscribe
        </Table.Cells.ButtonCell>
    )
}

export default function BrandItemsTableView(props: BrandItemsTableViewProps) {
    const { catalogId, brandParam, categories } = props

    return (
        <>
            <TopBar.Portal>
                <TopBar.Body>
                    <TopBar.BodyStart>
                        <BrandItemsTableViewTabs
                            catalogId={catalogId}
                            brandParam={brandParam}
                            categories={categories}
                        />
                    </TopBar.BodyStart>
                    <TopBar.BodyEnd>
                        <Table.SearchInput />
                        <Table.ViewMenu />
                    </TopBar.BodyEnd>
                </TopBar.Body>
            </TopBar.Portal>
            <Table.Table>
                <Table.Column<CatalogBrandItemRow>
                    accessorKey="thumbnail_url"
                    header="Image"
                    size={80}
                    cell={({ getValue, row }) => (
                        <Table.Cells.ImageCell src={getValue()} fallbackName={row.original.name} />
                    )}
                    enableSorting={false}
                    enablePinning={true}
                    dataType="other"
                />
                <Table.Column<CatalogBrandItemRow>
                    accessorKey="name"
                    header="Name"
                    size={288}
                    cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                    enableSorting={true}
                    enablePinning={true}
                    dataType="alphabetical"
                />
                <Table.Column<CatalogBrandItemRow>
                    accessorKey="sku"
                    header="SKU"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="alphabetical"
                />
                <Table.Column<CatalogBrandItemRow>
                    accessorKey="categories"
                    header="Categories"
                    size={240}
                    cell={({ row }) => (
                        <Table.Cells.BadgesCell badges={row.original.categories.map(getCategoryTitle)} />
                    )}
                    enableSorting={false}
                    enablePinning={false}
                    dataType="other"
                />
                <Table.Column<CatalogBrandItemRow>
                    accessorKey="list_price"
                    header="List Price"
                    size={128}
                    cell={({ getValue }) => <Table.Cells.NumericalDataCell value={getValue()} type="currency" />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
                <Table.Column<CatalogBrandItemRow>
                    accessorKey="customer_price"
                    header="Your Price"
                    size={128}
                    cell={({ getValue }) => <Table.Cells.NumericalDataCell value={getValue()} type="currency" />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
                <Table.Column<CatalogBrandItemRow>
                    accessorKey="id"
                    header="Subscription"
                    size={160}
                    cell={({ row }) => <SubscribeItemCell row={row.original} catalogId={catalogId} />}
                    enableSorting={false}
                    enablePinning={false}
                    dataType="other"
                />
            </Table.Table>
        </>
    )
}
